import { Component, inject, OnInit }                               from '@angular/core';
import { HttpErrorResponse }                                       from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router }                                  from '@angular/router';
import { MatInput }                                                from '@angular/material/input';
import { MatError, MatFormField, MatLabel }                        from '@angular/material/form-field';
import { MatProgressSpinner }                                      from '@angular/material/progress-spinner';

import { RegistrationData }               from '../../../../core/auth/auth.service';
import { PasswordTooltipDirective }       from '../../../../shared/directives/password-tooltip.directive';
import { PasswordUtils }                  from '../../../../shared/utils/password.utils';
import { FormUtils }                      from '../../../../shared/utils/form.utils';
import { StringUtils }                    from '../../../../shared/utils/string.utils';
import { RegistrationNotification }       from '../registration-notification';
import { asciiOnlyValidator }             from '../../../../shared/validators/ascii-only.validator';
import { AuthenticationService }          from '../../../../core/auth/authentication.service';
import { PinNotificationComponent }       from '../../../../shared/components/notification/notification.component';
import { TooltipComponent }               from '../../../../shared/components/tooltip/tooltip.component';
import { TrimFieldDirective }             from '../../../../shared/directives/trim-field.directive';
import { PasswordInputComponent }         from '../../../../shared/components/password-input/password-input.component';
import { PasswordInfoComponent }          from '../../../../shared/components/dialog/password-info/password-info.component';
import { ButtonComponent }                from '../../../../shared/components/buttons/button.component';
import { InternalRoutes }                 from '../../../../shared/data/internal-routes';
import { ErrorService }                   from '../../../../core/error-handling/error.service';

@Component({
  selector: 'pin-agent-registration-page',
  templateUrl: './agent-registration.component.html',
  hostDirectives: [PasswordTooltipDirective],
  imports: [PinNotificationComponent, ReactiveFormsModule, MatFormField, MatLabel, MatInput, MatError, TooltipComponent, TrimFieldDirective, PasswordInputComponent, PasswordInfoComponent, ButtonComponent, MatProgressSpinner]
})
export class AgentRegistrationComponent implements OnInit {
  readonly passwordTooltip = inject(PasswordTooltipDirective);
  returnUrl: string;
  invitationId: string;
  loading = false;
  registrationNotification: RegistrationNotification;
  registrationNotificationValues = RegistrationNotification;
  public agentRegistrationForm = new FormGroup(
    {
      accountIdentifier: new FormControl<string>('', [Validators.required]),
      firstName: new FormControl<string>('', [Validators.required, asciiOnlyValidator()]),
      lastName: new FormControl<string>('', [Validators.required, asciiOnlyValidator()]),
      username: new FormControl<string>('', [Validators.required,
        asciiOnlyValidator(StringUtils.USERNAME_ILLEGAL_CHARACTERS)]),
      password: new FormControl<string>('', [Validators.required,
        Validators.pattern(StringUtils.PASSWORD_VALIDATION_REGEX)]),
      passwordConfirmation: new FormControl<string>('', [Validators.required])
    },
    [PasswordUtils.samePasswordValidator]
  );
  private authService = inject(AuthenticationService);
  private errorService = inject(ErrorService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  ngOnInit() {
    this.invitationId = this.route.snapshot.queryParamMap.get('id');
  }

  sendRegistration() {
    if (!this.agentRegistrationForm.valid || this.loading) {
      FormUtils.markAllControlsTouchedAndValidate(this.agentRegistrationForm);
      return;
    }
    this.loading = true;
    const registerValue: RegistrationData = {
      ...this.agentRegistrationForm.getRawValue(),
      unifiedTaxIdLast4Digits: null,
      email: null,
      invitationId: this.invitationId
    }

    this.authService
      .register(registerValue)
      .subscribe({
        next: () => {
          this.loading = false;
          localStorage.setItem('registerUsername', registerValue.username);
          this.router.navigate(['/register'], {fragment: 'post-registration'}).then();
        },
        error: error => {
          this.loading = false;
          this.displayError(error);
        }
      });
  }

  onPasswordBlur() {
    this.passwordTooltip.onPasswordBlur();
    this.agentRegistrationForm.controls['passwordConfirmation'].updateValueAndValidity();
  }


  private displayNotification(notification: RegistrationNotification): void {
    this.registrationNotification = notification;
    window.scroll(0, 0);
  }

  private displayError(error: HttpErrorResponse) {
    if (this.errorService.isInfrastructureError(error)) {
      this.displayNotification(RegistrationNotification.try_again_later);
      return;
    }

    switch (error?.error?.message) {
      case 'ERR_USERNAME_ALREADY_EXISTS':
        this.displayNotification(RegistrationNotification.username_already_exists);
        break;
      case 'ERR_INVALID_ACCOUNT_NUMBER':
        this.displayNotification(RegistrationNotification.data_validation_error);
        break;
      case 'ERR_EXPIRED_INVITATION':
      case 'ERR_AGENT_ALREADY_REGISTERED':
      case 'ERR_UNEXPECTED':
        window.location.reload();
        break;
      default:
        this.router.navigate([InternalRoutes.ERROR]).then();
    }
  }
}
